import React from "react"
import styled, { css } from "styled-components"
import { useStaticQuery, graphql } from "gatsby"
import Link from "../shared/link"
import Breakpoints from "../shared/breakpoints"
import { T } from "../shared/typography.styles"
import SectionDivider from "../shared/section-divider"
import Grid from "../shared/grid"
import Img from "gatsby-image"
import VisiblityProvider from "../shared/visibility-provider"
import { FadeInBottom } from "../shared/keyframes.style"

const OuterGrid = styled(Grid)`
  background-color: var(--tricorn-black);
`

const LayoutGrid = styled(Grid)`
  grid-column: 1 / -1;
  justify-self: center;
  padding: 64px 0;
  @media (min-width: ${Breakpoints.md}) {
    padding: 48px 0;
  }
  @media (min-width: ${Breakpoints.md}) {
    grid-row: 1;
    align-self: center;
  }
`

const StyledDivider = styled(SectionDivider)`
  justify-self: center;
  background-color: #fff;

  @media (min-width: ${Breakpoints.lg}) {
    z-index: 1;
  }
`

const ImageAnimation = css`
  animation: ${(props) => !props.disableAnimations && FadeInBottom};
  animation-play-state: ${(props) => (props.isVisible ? "running" : "paused")};

  animation-duration: 1s;
  animation-timing-function: ease;
  animation-delay: 0.5s;
  animation-fill-mode: both;
`

const OuterImage = styled(Img)`
  display: none;
  max-height: 733px;

  @media (min-width: ${Breakpoints.lg}) {
    ${ImageAnimation}
    display: initial;
    grid-column: -7 / -2;
    grid-row: 1;
    z-index: 2000;
    align-self: center;
    margin: -128px 0;
  }
`

const InnerImage = styled(Img)`
  grid-column: 2 / -2;
  ${ImageAnimation}
  @media (min-width: ${Breakpoints.md}) {
    grid-column: -6 / -1;
  }
  @media (min-width: ${Breakpoints.lg}) {
    display: none;
  }
`

const InnerContent = styled.div`
  grid-column: 1 / -1;
  margin-top: 32px;
  display: flex;
  flex-direction: column;
  @media (min-width: ${Breakpoints.md}) {
    grid-column: 1 / 7;
    grid-row: 1;
    align-self: center;
    margin-top: 0;
  }
  @media (min-width: ${Breakpoints.lg}) {
    grid-column: 2 / 6;
    align-items: flex-start;
  }
`

const Title = styled.h2`
  ${T.sm.h2.bold}
  color: var(--dark-mode-body-grey);
  @media (min-width: ${Breakpoints.md}) {
    ${T.md.h2.bold}
  }
  @media (min-width: ${Breakpoints.lg}) {
    ${T.lg.h2.bold}
  }
`

const Description = styled.p`
  ${T.sm.body.regular}
  color: var(--dark-mode-body-grey);
  margin-top: 24px;
  @media (min-width: ${Breakpoints.md}) {
    ${T.md.body.regular}
  }
  @media (min-width: ${Breakpoints.lg}) {
    ${T.lg.body.regular}
  }
`

const StyledLink = styled(Link)`
  width: 100%;
  margin-top: 32px;
  @media (min-width: ${Breakpoints.md}) {
    width: auto;
  }
  @media (min-width: ${Breakpoints.lg}) {
    margin-top: 48px;
  }
`

export default function MapsShowcaseSection(props) {
  const query = useStaticQuery(graphql`
    query {
      kioskImage: file(
        relativePath: {
          eq: "1_SOLUTIONS/Indoor Mapping/showcase_indoor_maps.png"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 700, quality: 85) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
    }
  `)

  return (
    <>
      <StyledDivider />
      <OuterGrid>
        <LayoutGrid>
          <VisiblityProvider>
            <InnerImage
              fluid={query.kioskImage.childImageSharp.fluid}
              imgStyle={{ objectFit: "contain" }}
            />
          </VisiblityProvider>
          <InnerContent>
            <Title>Showcase Your Indoor Maps</Title>
            <Description>
              With a mapping platform to manage your map and real-time updates,
              you can be confident that employees and visitors to your venue are
              using accurate representations, wayfinding, and accessing the most
              up-to-date location information. Deploy a mapping experience that
              makes sense for your venue and buildings, from exports to Digital
              Directory touchscreens.
            </Description>
            <StyledLink href="/wayfinding/digital-directory/">
              Explore Digital Directory
            </StyledLink>
          </InnerContent>
        </LayoutGrid>
        <VisiblityProvider>
          <OuterImage
            fluid={query.kioskImage.childImageSharp.fluid}
            imgStyle={{ objectFit: "contain" }}
          />
        </VisiblityProvider>
      </OuterGrid>
      <StyledDivider flip />
    </>
  )
}
